
import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { 
  getCachedRedirect, 
  cacheRedirect, 
  normalizePath,
  clearRedirectCacheByPattern
} from '@/utils/redirectUtils';
import { toast } from 'sonner';

export const Redirect = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastProcessedPath, setLastProcessedPath] = useState<string | null>(null);
  const path = location.pathname;
  const processingRef = useRef<boolean>(false);
  
  // Helper function to check if a path is likely a static file
  const isLikelyStaticFile = (path: string): boolean => {
    const staticExtensions = [
      '.js', '.css', '.svg', '.jpg', '.jpeg', '.png', '.gif', '.webp', 
      '.ico', '.woff', '.woff2', '.ttf', '.eot', '.pdf', '.doc', '.docx'
    ];
    return staticExtensions.some(ext => path.toLowerCase().endsWith(ext));
  };
  
  // Helper to check if path is in admin section
  const isAdminPath = (path: string): boolean => {
    return path.startsWith('/admin');
  };
  
  // Helper to check if a path has a trailing slash (except root path)
  const hasTrailingSlash = (path: string): boolean => {
    return path !== '/' && path.endsWith('/');
  };

  // Cache metrics - simplified to reduce memory usage
  const logRedirectStatus = (status: string) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Redirect] ${status}`);
    }
  };
  
  // Clear any admin redirects when component mounts
  useEffect(() => {
    if (isAdminPath(path)) {
      clearRedirectCacheByPattern('/admin');
    }
    
    // Special handling for master-services-agreement paths
    if (path === '/master-services-agreement/' || path === '/master-services-agreement') {
      // Force clear any cached redirect for both versions
      cacheRedirect('/master-services-agreement/', undefined);
      cacheRedirect('/master-services-agreement', undefined);
      // Log that we're bypassing redirect for this path
      logRedirectStatus(`Bypassing redirect for ${path} as it exists in routes`);
    }
  }, [path]);
  
  useEffect(() => {
    const handleRedirect = async () => {
      // Skip if already processing or same path
      if (processingRef.current || lastProcessedPath === path) {
        return;
      }
      
      // Skip processing for known valid routes (both with and without trailing slashes)
      if (path === '/master-services-agreement/' || path === '/master-services-agreement') {
        logRedirectStatus(`Skipping redirect for known route: ${path}`);
        return;
      }
      
      // Skip processing for admin paths, special paths, and static files to improve performance
      if (
        isAdminPath(path) ||
        path === '/404' || 
        path.startsWith('/tools/security-field-guide') ||
        path === '/sitemap.xml' ||
        path.startsWith('/api/') ||
        path.startsWith('/__supabase/') ||
        isLikelyStaticFile(path)
      ) {
        return;
      }
      
      try {
        logRedirectStatus(`Processing path: ${path}`);
        processingRef.current = true;
        setIsProcessing(true);
        setLastProcessedPath(path);
        
        // Normalize the path
        const normalizedPath = normalizePath(path);

        // Check cache first for better performance
        const cachedRedirect = getCachedRedirect(normalizedPath);
        if (cachedRedirect !== undefined) {
          if (cachedRedirect) {
            logRedirectStatus(`Cache hit: ${path} → ${cachedRedirect}`);
            navigate(cachedRedirect, { replace: true });
          } else {
            logRedirectStatus(`Cache hit: No redirect needed for ${path}`);
          }
          processingRef.current = false;
          setIsProcessing(false);
          return;
        }
        
        logRedirectStatus(`Cache miss: Checking redirect for ${path}`);
        
        try {
          const { data, error } = await supabase.functions.invoke('unified-redirector', {
            body: { path: path },  // Use the original path with trailing slash if present
          });
          
          if (error) {
            console.error('[Redirect] Edge function error:', error);
            
            // Only show error toast in development
            if (process.env.NODE_ENV === 'development') {
              toast.error(`Redirect error: ${error.message || 'Unknown error'}`);
            }
            processingRef.current = false;
            setIsProcessing(false);
            return;
          }
          
          // If it's a found redirect
          if (data?.found === true && data?.Location) {
            logRedirectStatus(`Found redirect: ${path} → ${data.Location}`);
            // Cache the result for future use
            cacheRedirect(normalizedPath, data.Location, data.statusCode || 301);
            navigate(data.Location, { replace: true });
            return;
          } 
          
          // If it's explicitly marked as not found
          if (data?.found === false || data?.message === "No redirect found") {
            logRedirectStatus(`No redirect found for: ${path}`);
            // Cache negative result to avoid unnecessary server calls
            cacheRedirect(normalizedPath, null);
          }
        } catch (error) {
          console.error('[Redirect] Failed to check redirect:', error);
          
          // In development, show error toast
          if (process.env.NODE_ENV === 'development') {
            toast.error(`Failed to check redirect: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }
      } catch (error) {
        console.error('[Redirect] Error processing redirect:', error);
      } finally {
        processingRef.current = false;
        setIsProcessing(false);
      }
    };

    // Only process redirects if not already handling one
    if (!isProcessing) {
      handleRedirect();
    }
  }, [location.pathname, navigate, isProcessing, lastProcessedPath, path]);

  // Render nothing - this is just a side-effect component
  return null;
};
